import { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Plus, Flag, Trash2, PencilLine, CheckCircle2, Clock3 } from "lucide-react";
import { useConfirm } from "../ConfirmProvider";

const MS_STATUSES = ["Planned", "In Progress", "Completed", "Delayed"];
const STATUS_STYLES = {
  "Planned": "bg-neutral-100 text-neutral-700 border-neutral-300",
  "In Progress": "bg-blue-50 text-blue-700 border-blue-300",
  "Completed": "bg-emerald-50 text-emerald-700 border-emerald-300",
  "Delayed": "bg-rose-50 text-rose-700 border-rose-300",
};

const EMPTY = { name: "", description: "", due_date: "", status: "Planned", order: 0, client_approval_required: false };

export default function MilestonesPanel({ projectId, canEdit, onChanged }) {
  const confirm = useConfirm();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const { data } = await api.get(`/projects/${projectId}/milestones`); setItems(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, [projectId]);

  const openNew = () => { setEditing(null); setForm({ ...EMPTY, order: items.length }); setOpen(true); };
  const openEdit = (m) => { setEditing(m); setForm({ ...EMPTY, ...m }); setOpen(true); };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setBusy(true);
    try {
      if (editing) await api.put(`/milestones/${editing.id}`, form);
      else await api.post(`/projects/${projectId}/milestones`, form);
      toast.success(editing ? "Milestone updated" : "Milestone added");
      setOpen(false); load(); onChanged?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const remove = async (m) => {
    if (!(await confirm({ title: `Delete milestone "${m.name}"?`, description: "This cannot be undone." }))) return;
    try { await api.delete(`/milestones/${m.id}`); toast.success("Milestone deleted"); load(); onChanged?.(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const setStatus = async (m, status) => {
    try { await api.put(`/milestones/${m.id}`, { status }); load(); onChanged?.(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-3" data-testid="milestones-panel">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-orange-400">Delivery Checkpoints</div>
          <h3 className="text-lg font-bold">Milestones ({items.length})</h3>
        </div>
        {canEdit && (
          <Button onClick={openNew} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="add-milestone-btn">
            <Plus className="w-4 h-4 mr-1" /> Add Milestone
          </Button>
        )}
      </div>

      {items.length === 0 && (
        <div className="panel p-8 text-center text-neutral-500 text-sm">
          <Flag className="w-6 h-6 mx-auto mb-2 opacity-50" />
          No milestones yet. {canEdit && "Break the project into checkpoints to track delivery."}
        </div>
      )}

      <div className="space-y-2">
        {items.map((m) => (
          <div key={m.id} className="panel p-4 flex items-start gap-4" data-testid={`milestone-${m.id}`}>
            <div className="w-9 h-9 shrink-0 rounded-lg bg-orange-50 border border-orange-200 grid place-items-center">
              {m.status === "Completed" ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <Flag className="w-4 h-4 text-orange-600" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[10px] font-mono text-neutral-500">{m.milestone_id}</span>
                <span className={`text-[10px] font-mono px-2 py-0.5 rounded border ${STATUS_STYLES[m.status] || ""}`}>{m.status}</span>
                {m.due_date && <span className="text-[10px] font-mono text-neutral-500 flex items-center gap-1"><Clock3 className="w-3 h-3" /> {m.due_date}</span>}
                {m.client_approval_required && !m.client_approved && m.status === "Completed" && (
                  <span className="text-[10px] font-mono px-2 py-0.5 rounded border bg-amber-50 text-amber-700 border-amber-300">Awaiting Client Sign-off</span>
                )}
                {m.client_approval_required && !m.client_approved && m.status !== "Completed" && (
                  <span className="text-[10px] font-mono px-2 py-0.5 rounded border bg-neutral-100 text-neutral-600 border-neutral-300">Needs Client Sign-off</span>
                )}
                {m.client_approved && (
                  <span className="text-[10px] font-mono px-2 py-0.5 rounded border bg-emerald-50 text-emerald-700 border-emerald-300" title={`Signed by ${m.client_signature || "client"} on ${m.client_approved_at || ""}`}>
                    ✓ Approved by {m.client_signature || "client"}
                  </span>
                )}
              </div>
              <div className="font-semibold text-sm mt-1">{m.name}</div>
              {m.description && <div className="text-xs text-neutral-600 mt-1 whitespace-pre-wrap">{m.description}</div>}
            </div>
            {canEdit && (
              <div className="flex items-center gap-1">
                <Select value={m.status} onValueChange={(v) => setStatus(m, v)}>
                  <SelectTrigger className="h-8 w-32 text-xs bg-white border-neutral-200" data-testid={`milestone-status-${m.id}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>{MS_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
                <Button variant="ghost" size="icon" onClick={() => openEdit(m)} data-testid={`milestone-edit-${m.id}`}><PencilLine className="w-4 h-4" /></Button>
                <Button variant="ghost" size="icon" onClick={() => remove(m)} className="text-rose-500 hover:text-rose-700" data-testid={`milestone-delete-${m.id}`}><Trash2 className="w-4 h-4" /></Button>
              </div>
            )}
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white border-neutral-200 max-w-lg" data-testid="milestone-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit Milestone" : "New Milestone"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-[10px] uppercase font-mono tracking-wider text-neutral-500">Name</Label>
              <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="ms-name" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[10px] uppercase font-mono tracking-wider text-neutral-500">Description</Label>
              <Textarea rows={3} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} data-testid="ms-desc" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-[10px] uppercase font-mono tracking-wider text-neutral-500">Due Date</Label>
                <Input type="date" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })} data-testid="ms-due" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[10px] uppercase font-mono tracking-wider text-neutral-500">Status</Label>
                <Select value={form.status} onValueChange={v => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="ms-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{MS_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <label className="flex items-start gap-2 mt-1 cursor-pointer p-3 rounded-lg border border-neutral-200 bg-neutral-50 hover:border-neutral-300" data-testid="ms-approval-toggle">
              <input type="checkbox" checked={!!form.client_approval_required} onChange={e => setForm({ ...form, client_approval_required: e.target.checked })} className="mt-0.5 accent-orange-500" />
              <div>
                <div className="text-sm font-semibold">Require client sign-off</div>
                <div className="text-[11px] text-neutral-600">When marked Completed, the client will be prompted to approve this checkpoint from their portal before you invoice.</div>
              </div>
            </label>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={busy || !form.name.trim()} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="ms-save">
              {busy ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
